/**
 * signatureEngine.js — variable-width smoothed signature drawing on <canvas> (OMM-616).
 *
 * Clean-room implementation. Three things make a stroke read as handwriting:
 *
 *   1. A SYMMETRIC neighbour filter over the captured points. Each point is nudged toward
 *      the average of the point before and after it, so tremor cancels without the path
 *      lagging behind the pen. (A one-sided/exponential filter is the usual shortcut here,
 *      and it is what flattens the entry of a curve and then kinks when it catches up.)
 *      Because it needs the *next* point, painting runs a couple of samples behind the
 *      pointer and flushes on lift — a few milliseconds, invisible in use.
 *   2. Centripetal Catmull-Rom fitting (alpha = 0.5) through those points, converted to
 *      cubic Beziers. Centripetal parameterisation is what stops the overshoot and cusps
 *      that uniform Catmull-Rom produces when fast (long) and slow (short) segments meet.
 *   3. Velocity-driven width with its own easing, so fast strokes taper thin.
 *
 * Stored points keep a speed factor, not a baked width: nib settings re-render existing ink.
 * Framework-agnostic, no dependencies. Pure geometry helpers are exported for unit tests.
 */

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const lerp = (a, b, t) => a + (b - a) * t;
const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

export const DEFAULTS = {
  penColor: '#333333',
  backgroundColor: 'transparent', // used by clear() and by JPEG/WebP export
  minWidth: 2.5, // px, at the stroke's own top speed
  maxWidth: 4.25, // px, at rest
  smoothing: 0.85, // 0..1 — strength of the symmetric point filter
  curveTension: 1, // 0 = polyline, 1 = full Catmull-Rom curvature
  speedSensitivity: 0.4, // 0..1 — how strongly speed thins the stroke
  widthEasing: 0.35, // 0..1 — how fast width reacts to speed changes
  taper: [0.72, 0.46, 0.24], // width multipliers for the last points of a stroke
  startTaper: [0.5, 0.74, 0.9], // width multipliers for the first points of a stroke
  pressureSensitivity: 0.5, // 0..1 — stylus pressure influence (mouse/finger ignored)
  minDistance: 1, // px between kept samples; kills stationary jitter
  onBegin: null,
  onEnd: null,
};

/**
 * One Catmull-Rom span (p1 -> p2) as a cubic Bezier, centripetal by default.
 * `tension` blends between the straight chord (0) and the full curve (1).
 */
export function toBezier(p0, p1, p2, p3, tension, alpha = 0.5) {
  const d1 = Math.max(dist(p0, p1), 1e-6) ** alpha;
  const d2 = Math.max(dist(p1, p2), 1e-6) ** alpha;
  const d3 = Math.max(dist(p2, p3), 1e-6) ** alpha;

  const axis = k => {
    const a1 = d1 * d1;
    const b1 = d2 * d2;
    const n1 = 2 * a1 + 3 * d1 * d2 + b1;
    const e1 = 3 * d1 * (d1 + d2);
    let c1 = e1 > 1e-9 ? (a1 * p2[k] - b1 * p0[k] + n1 * p1[k]) / e1 : p1[k];

    const a2 = d3 * d3;
    const b2 = d2 * d2;
    const n2 = 2 * a2 + 3 * d3 * d2 + b2;
    const e2 = 3 * d3 * (d3 + d2);
    let c2 = e2 > 1e-9 ? (a2 * p1[k] - b2 * p3[k] + n2 * p2[k]) / e2 : p2[k];

    // Blend toward the straight chord when tension < 1.
    c1 = lerp(p1[k] + (p2[k] - p1[k]) / 3, c1, tension);
    c2 = lerp(p2[k] - (p2[k] - p1[k]) / 3, c2, tension);
    return [c1, c2];
  };

  const [c1x, c2x] = axis('x');
  const [c1y, c2y] = axis('y');
  return { x0: p1.x, y0: p1.y, c1x, c1y, c2x, c2y, x1: p2.x, y1: p2.y };
}

export function bezierPoint(b, t) {
  const u = 1 - t;
  const a = u * u * u;
  const c = 3 * u * u * t;
  const d = 3 * u * t * t;
  const e = t * t * t;
  return {
    x: a * b.x0 + c * b.c1x + d * b.c2x + e * b.x1,
    y: a * b.y0 + c * b.c1y + d * b.c2y + e * b.y1,
  };
}

/** Control-polygon length — a cheap upper bound, used only to pick a step count. */
function bezierLength(b) {
  return (
    Math.hypot(b.c1x - b.x0, b.c1y - b.y0) +
    Math.hypot(b.c2x - b.c1x, b.c2y - b.c1y) +
    Math.hypot(b.x1 - b.c2x, b.y1 - b.c2y)
  );
}

/** Mirror a point across another — gives stroke ends a natural tangent. */
const reflect = (p, about) => ({ ...about, x: 2 * about.x - p.x, y: 2 * about.y - p.y });

/** Filter passes implied by `smoothing`. 0 => raw points. */
const smoothingPasses = smoothing => {
  const s = clamp(smoothing, 0, 1);
  return s <= 0.001 ? 0 : Math.max(1, Math.round(s * 3));
};

/**
 * Symmetric 3-tap filter, endpoints pinned. Each pass moves a point a fraction of the
 * way toward the midpoint of its neighbours — no phase lag, so the curve keeps the
 * shape the hand drew instead of trailing it.
 */
export function smoothPoints(pts, smoothing) {
  const passes = smoothingPasses(smoothing);
  if (!passes || pts.length < 3) return pts;
  const k = 0.5 * clamp(smoothing * 1.15, 0, 1);
  let cur = pts;
  for (let n = 0; n < passes; n++) {
    const next = cur.slice();
    for (let i = 1; i < cur.length - 1; i++) {
      const a = cur[i - 1];
      const b = cur[i];
      const c = cur[i + 1];
      next[i] = {
        ...b,
        x: b.x + ((a.x + c.x) / 2 - b.x) * k,
        y: b.y + ((a.y + c.y) / 2 - b.y) * k,
      };
    }
    cur = next;
  }
  return cur;
}

/**
 * The speed that counts as "fast" for THIS stroke — its own 80th percentile.
 *
 * A fixed px/ms threshold flattens the contrast: it has to suit a small signature box and
 * a large one, a quick scrawl and a careful hand, and ends up pinning most of a given
 * stroke to one end of the width range. Normalising per stroke means every signature uses
 * the full nib range: thick where that person slowed, hairline where they flew.
 */
export function speedRef(pts) {
  if (pts.length < 4) return 1;
  const vs = pts
    .map(p => p.v ?? 0)
    .filter(v => v > 0)
    .sort((a, b) => a - b);
  if (!vs.length) return 1;
  const ref = vs[Math.floor(vs.length * 0.8)];
  return clamp(ref, 0.08, 8);
}

/**
 * Resolve a point to a stroke radius using the given nib settings.
 *
 * The speed -> width map is reciprocal, not linear: width = maxWidth / (1 + speed * k).
 * A linear ramp spends most of its range on speeds the hand rarely holds, so the stroke
 * comes out nearly uniform. The reciprocal keeps full weight through slow curves and
 * drops toward a hairline the moment the pen accelerates.
 */
export function strokeRadius(p, ref, options) {
  const { minWidth, maxWidth, speedSensitivity, pressureSensitivity } = options;
  const k = clamp(speedSensitivity, 0, 1) * 4.5;
  const norm = p.v != null ? clamp(p.v / ref, 0, 1.15) : 0;
  let width = maxWidth / (1 + norm * k);
  if (p.p != null && p.p > 0 && p.p !== 0.5) {
    width *= lerp(1, p.p * 2, clamp(pressureSensitivity, 0, 1));
  }
  width = clamp(width, minWidth, maxWidth * 1.5) * (p.e ?? 1);
  return Math.max(0.05, width / 2);
}

export class SignatureEngine {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {Partial<typeof DEFAULTS>} options
   */
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.options = { ...DEFAULTS, ...options };

    /** @type {Array<Array<{x:number,y:number,v:number,p?:number,e?:number}>>} raw strokes */
    this.strokes = [];
    this._current = null; // raw points of the stroke in progress
    this._paintedTo = 0; // index of the first span not yet painted
    this._lastSpeed = null;
    this._lastMoveTime = 0;
    this._drawing = false;
    this._pointerId = null;
    this._dpr = 1;
    this._cache = new WeakMap(); // stroke -> { key, pts } smoothed result

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointerdown', this._onPointerDown);
    canvas.addEventListener('pointermove', this._onPointerMove);
    canvas.addEventListener('pointerup', this._onPointerUp);
    canvas.addEventListener('pointercancel', this._onPointerUp);
    // Fallback only: while pointer capture holds, leave does not fire mid-stroke.
    canvas.addEventListener('pointerleave', this._onPointerUp);

    this._ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.resize()) : null;
    if (this._ro) this._ro.observe(canvas);

    this.resize();
  }

  // ---------------------------------------------------------------- lifecycle

  destroy() {
    const c = this.canvas;
    c.removeEventListener('pointerdown', this._onPointerDown);
    c.removeEventListener('pointermove', this._onPointerMove);
    c.removeEventListener('pointerup', this._onPointerUp);
    c.removeEventListener('pointercancel', this._onPointerUp);
    c.removeEventListener('pointerleave', this._onPointerUp);
    if (this._ro) this._ro.disconnect();
  }

  setOptions(patch) {
    const visual = [
      'penColor',
      'backgroundColor',
      'smoothing',
      'curveTension',
      'minWidth',
      'maxWidth',
      'speedSensitivity',
      'pressureSensitivity',
    ];
    const needsRedraw = visual.some(k => k in patch && patch[k] !== this.options[k]);
    Object.assign(this.options, patch);
    if (needsRedraw) this.redraw();
  }

  resize() {
    const c = this.canvas;
    const rect = c.getBoundingClientRect();
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
    const w = Math.round((rect.width || c.clientWidth || 300) * dpr);
    const h = Math.round((rect.height || c.clientHeight || 150) * dpr);
    if (c.width !== w || c.height !== h || dpr !== this._dpr) {
      this._dpr = dpr;
      c.width = w;
      c.height = h;
      this.redraw();
    }
  }

  // ------------------------------------------------------------------- public

  isEmpty() {
    return this.strokes.length === 0;
  }

  clear() {
    this.strokes = [];
    this._current = null;
    this.redraw();
  }

  toDataURL(type = 'image/png', quality) {
    if (type === 'image/jpeg' || type === 'image/webp') {
      const off = document.createElement('canvas');
      off.width = this.canvas.width;
      off.height = this.canvas.height;
      const ctx = off.getContext('2d');
      ctx.fillStyle =
        this.options.backgroundColor === 'transparent' ? '#ffffff' : this.options.backgroundColor;
      ctx.fillRect(0, 0, off.width, off.height);
      ctx.drawImage(this.canvas, 0, 0);
      return off.toDataURL(type, quality);
    }
    return this.canvas.toDataURL(type, quality);
  }

  /**
   * Tight crop of the ink. Returns null when empty. Signature widgets render the PNG with
   * contain-fit, so trimming the whitespace is what makes the ink fill the field box.
   */
  toTrimmedDataURL(type = 'image/png', padding = 4) {
    if (this.isEmpty()) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const stroke of this.strokes) {
      const pts = this._smooth(stroke);
      const ref = speedRef(pts);
      for (const p of pts) {
        const r = strokeRadius(p, ref, this.options);
        minX = Math.min(minX, p.x - r);
        maxX = Math.max(maxX, p.x + r);
        minY = Math.min(minY, p.y - r);
        maxY = Math.max(maxY, p.y + r);
      }
    }
    const dpr = this._dpr;
    const x = Math.max(0, (minX - padding) * dpr);
    const y = Math.max(0, (minY - padding) * dpr);
    const w = Math.min(this.canvas.width - x, (maxX - minX + padding * 2) * dpr);
    const h = Math.min(this.canvas.height - y, (maxY - minY + padding * 2) * dpr);
    const off = document.createElement('canvas');
    off.width = Math.max(1, Math.round(w));
    off.height = Math.max(1, Math.round(h));
    const ctx = off.getContext('2d');
    if (this.options.backgroundColor !== 'transparent') {
      ctx.fillStyle = this.options.backgroundColor;
      ctx.fillRect(0, 0, off.width, off.height);
    }
    ctx.drawImage(this.canvas, x, y, w, h, 0, 0, off.width, off.height);
    return off.toDataURL(type);
  }

  // ---------------------------------------------------------------- smoothing

  /** Memoised per stroke, invalidated when the smoothing settings change. */
  _smooth(stroke) {
    const key = `${this.options.smoothing}:${stroke.length}`;
    const hit = this._cache.get(stroke);
    if (hit && hit.key === key) return hit.pts;
    const pts = smoothPoints(stroke, this.options.smoothing);
    this._cache.set(stroke, { key, pts });
    return pts;
  }

  // ------------------------------------------------------------------ drawing

  _ctx() {
    const ctx = this.canvas.getContext('2d');
    ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
    return ctx;
  }

  redraw() {
    const ctx = this._ctx();
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (this.options.backgroundColor && this.options.backgroundColor !== 'transparent') {
      ctx.fillStyle = this.options.backgroundColor;
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
    ctx.restore();

    const all =
      this._current && this._current.length ? this.strokes.concat([this._current]) : this.strokes;

    for (const stroke of all) {
      const pts =
        stroke === this._current
          ? smoothPoints(stroke, this.options.smoothing)
          : this._smooth(stroke);
      this._paintSpans(ctx, pts, 0, pts.length - 2);
    }
    this._paintedTo = this._current ? Math.max(0, this._current.length - 1) : 0;
  }

  /** Paint spans [from..to] of an already-smoothed point list. */
  _paintSpans(ctx, pts, from, to, refOverride) {
    if (!pts.length) return;
    ctx.fillStyle = this.options.penColor;

    if (pts.length === 1) {
      const r = this.options.maxWidth / 2;
      ctx.beginPath();
      ctx.arc(pts[0].x, pts[0].y, r, 0, Math.PI * 2);
      ctx.fill();
      return;
    }

    const tension = clamp(this.options.curveTension, 0, 1);
    const ref = refOverride ?? speedRef(pts);
    ctx.beginPath();
    for (let i = Math.max(0, from); i <= Math.min(to, pts.length - 2); i++) {
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p0 = pts[i - 1] || reflect(p2, p1);
      const p3 = pts[i + 2] || reflect(p1, p2);
      this._addSpan(
        ctx,
        toBezier(p0, p1, p2, p3, tension),
        strokeRadius(p1, ref, this.options),
        strokeRadius(p2, ref, this.options),
      );
    }
    ctx.fill();
  }

  /**
   * Stamp discs along the curve with interpolated radii. One path + one fill per call, so
   * overlapping stamps never double-blend and the edge stays clean at any opacity.
   */
  _addSpan(ctx, b, r1, r2) {
    // Spacing must stay below the stamp radius or the discs separate and the line beads.
    const spacing = clamp(Math.min(r1, r2) * 0.5, 0.25, 1.2);
    const steps = clamp(Math.ceil(bezierLength(b) / spacing), 2, 600);
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const pt = bezierPoint(b, t);
      const r = Math.max(0.05, lerp(r1, r2, t));
      ctx.moveTo(pt.x + r, pt.y);
      ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
    }
  }

  /**
   * Paint whatever of the live stroke is now settled. The symmetric filter needs one
   * neighbour per pass ahead of a point, and a span needs one point beyond its end, so
   * painting trails the pointer by (passes + 1) samples until the pen lifts.
   */
  _paintProgress(final) {
    const raw = this._current;
    if (!raw || raw.length < 2) {
      if (final) this.redraw();
      return;
    }
    const pts = smoothPoints(raw, this.options.smoothing);
    const lag = smoothingPasses(this.options.smoothing) + 1;
    const last = final ? pts.length - 2 : pts.length - 2 - lag;
    if (last < this._paintedTo) return;
    // Live reference only ever grows, so already-painted spans don't disagree with new ones.
    // The redraw on pen-up settles the whole stroke against its final speed range.
    this._liveRef = Math.max(this._liveRef || 0, speedRef(pts));
    this._paintSpans(this._ctx(), pts, this._paintedTo, last, this._liveRef);
    this._paintedTo = last + 1;
  }

  // -------------------------------------------------------------------- input

  _pos(e) {
    const rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  /**
   * Eased pen speed in px/ms, stored raw on the point. Normalisation happens at paint time
   * against the stroke's own speed range — see speedRef.
   */
  _speed(point, dt) {
    const prev = this._current[this._current.length - 1];
    const d = prev ? dist(prev, point) : 0;
    const speed = dt > 0 ? d / dt : 0;
    const eased =
      this._lastSpeed == null
        ? speed
        : lerp(this._lastSpeed, speed, clamp(this.options.widthEasing, 0.05, 1));
    this._lastSpeed = eased;
    return eased;
  }

  _onPointerDown(e) {
    if (e.button !== undefined && e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    this._pointerId = e.pointerId;
    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch {
      /* not fatal */
    }
    this._drawing = true;
    this._lastSpeed = null;
    this._lastMoveTime = e.timeStamp;
    this._current = [];
    this._liveRef = 0;
    const p = this._pos(e);
    this._current.push({ ...p, v: this._speed(p, 0), p: e.pressure });
    this._paintedTo = 0;
    this.redraw();
    this.options.onBegin?.(e);
  }

  _onPointerMove(e) {
    if (!this._drawing || e.pointerId !== this._pointerId) return;
    e.preventDefault();

    // Coalesced events recover the samples the browser batched into this frame.
    // Note: getCoalescedEvents() may return an EMPTY array (untrusted events, some
    // platforms) — an empty array is truthy, so guard on length.
    const coalesced = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : null;
    const events = coalesced && coalesced.length ? coalesced : [e];

    for (const ev of events) {
      const step = Math.max(1, ev.timeStamp - this._lastMoveTime);
      const p = this._pos(ev);
      const prev = this._current[this._current.length - 1];
      // Skipped samples must NOT advance _lastMoveTime: velocity for the next kept
      // point has to span the full elapsed time since the previous kept point, or
      // slow strokes read as fast and get over-thinned.
      if (prev && dist(prev, p) < this.options.minDistance) continue;
      this._current.push({ ...p, v: this._speed(p, step), p: ev.pressure });
      this._lastMoveTime = ev.timeStamp;
    }
    this._paintProgress(false);
  }

  _onPointerUp(e) {
    if (!this._drawing || (this._pointerId !== null && e.pointerId !== this._pointerId)) return;
    this._drawing = false;
    this._pointerId = null;
    try {
      this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* not fatal */
    }

    const pts = this._current;
    if (pts && pts.length) {
      // The first sample has no previous point, so its speed reads as zero and it would
      // paint at full nib width — a blob where the pen touched down. Borrow the next
      // sample's speed, then taper both ends.
      if (pts.length > 1) pts[0].v = pts[1].v;
      const taper = this.options.taper || [];
      for (let i = 0; i < taper.length && pts.length > taper.length + 1; i++) {
        pts[pts.length - taper.length + i].e = taper[i];
      }
      const head = this.options.startTaper || [];
      for (let i = 0; i < head.length && pts.length > head.length + 1; i++) {
        pts[i].e = head[i];
      }
      this._paintProgress(true);
      this.strokes.push(pts);
    }
    this._current = null;
    this._paintedTo = 0;
    this.redraw();
    this.options.onEnd?.(e);
  }
}

export default SignatureEngine;
